/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @file ExportProgressUtils.js
 * @author Gladson Samuel S
 * @description Report/API naming for the "Export student progress" CSV reports served by
 * Marin GET /aet/courses/{courseId}/assignments/reports.
 *
 * Supported reports, with the filename each one downloads as:
 * - L1 assignments   ?sheet=assignment-progress
 *                    biology-101-assignments-progress.csv
 * - L2 assignment    ?sheet=assignment-progress&assignmentId={assignmentId}
 *                    biology-101-assignment-cell-structure-progress.csv
 * - L1 students      ?sheet=assignment-progress-by-students
 *                    biology-101-students-progress.csv
 * - L2 student       ?sheet=assignment-progress-by-student&studentId={studentId}
 *                    biology-101-student-john-smith-progress.csv
 */

import { buildExportFilename } from './FileDownloadUtils';

/**
 * `sheet` values accepted by the reports endpoint
 */
export const EXPORT_PROGRESS_SHEET = {
  ASSIGNMENT_PROGRESS: 'assignment-progress',
  ASSIGNMENT_PROGRESS_BY_STUDENT: 'assignment-progress-by-student',
  ASSIGNMENT_PROGRESS_BY_STUDENTS: 'assignment-progress-by-students'
};

/**
 * Query param names accepted by the reports endpoint
 */
export const EXPORT_PROGRESS_PARAM = {
  SHEET: 'sheet',
  ASSIGNMENT_ID: 'assignmentId',
  STUDENT_ID: 'studentId'
};

/**
 * Param names appended, in order, after `sheet`
 */
const EXPORT_PROGRESS_SCOPE_PARAMS = [
  EXPORT_PROGRESS_PARAM.ASSIGNMENT_ID,
  EXPORT_PROGRESS_PARAM.STUDENT_ID
];

/**
 * What each report covers, used as the filename segment after the course name
 */
export const EXPORT_PROGRESS_SCOPE = {
  ASSIGNMENTS: 'assignments',
  ASSIGNMENT: 'assignment',
  STUDENTS: 'students',
  STUDENT: 'student'
};

/**
 * Trailing filename segment shared by every export progress report
 */
const EXPORT_PROGRESS_FILENAME_SUFFIX = 'progress';

/**
 * Longest a single sanitized name segment may be, so a long course or assignment
 * title cannot push the filename past the filesystem limit
 */
const FILENAME_SEGMENT_MAX_LENGTH = 60;

/**
 * Builds the reports request path for the given course and report scope
 *
 * @param {string} courseId - The ID of the course
 * @param {Object} queryParams - `sheet` plus an optional `assignmentId` / `studentId` scope
 * @returns {string} The reports request path, relative to the Marin client base URL
 */
export function buildExportProgressReportPath(courseId, queryParams = {}) {
  const search = new URLSearchParams();

  search.set(EXPORT_PROGRESS_PARAM.SHEET, queryParams[EXPORT_PROGRESS_PARAM.SHEET] || '');

  EXPORT_PROGRESS_SCOPE_PARAMS.forEach((param) => {
    if (queryParams[param]) {
      search.set(param, queryParams[param]);
    }
  });

  return `/aet/courses/${courseId}/assignments/reports?${search.toString()}`;
}

/**
 * Turns a display name into a filename-safe, lowercased, hyphen separated segment.
 * Letters and digits of any script are kept; everything else collapses to a single hyphen.
 *
 * @param {string} value - A display name, e.g. a course, assignment or student name
 * @returns {string} The sanitized segment, empty when the name has nothing usable
 */
export function sanitizeFilenameSegment(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, FILENAME_SEGMENT_MAX_LENGTH)
    .replace(/-+$/, '');
}

/**
 * Builds the filename for the downloaded report, e.g.
 * `biology-101-assignment-cell-structure-progress.csv`. The scope name keeps L1 and L2
 * exports of the same sheet from sharing a filename.
 *
 * @param {string} courseName - The course display name
 * @param {string} scope - One of {@link EXPORT_PROGRESS_SCOPE}
 * @param {string} [scopeName] - The assignment or student display name, for the L2 reports
 * @returns {string} The filename
 */
export function buildExportProgressFilename(courseName, scope, scopeName) {
  return buildExportFilename(
    sanitizeFilenameSegment(courseName),
    scope,
    sanitizeFilenameSegment(scopeName),
    EXPORT_PROGRESS_FILENAME_SUFFIX
  );
}
